import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Coffee, Send, Package, Mail, Phone, Globe, Sparkles, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";
import { DEFAULT_SETTINGS, fetchSettings } from "@/siteSettings";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIETY_OPTIONS = ["Geisha", "Gourmet", "Lavado", "Honey"];

const inputCls =
  "w-full border border-pine/15 bg-white px-4 py-3 text-sm font-light text-bark placeholder:text-sand/60 focus:border-copper focus:outline-none transition-colors duration-300";

export default function Contact() {
  const { t, lang } = useI18n();
  const c = t.contact;
  const f = t.fields;

  const [form, setForm] = useState({
    name: "",
    business: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    country: "",
    volume: c.volumes[0],
    varieties: [],
    message: "",
    sample_box: false,
  });
  const [cups, setCups] = useState(300);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [aiDesc, setAiDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAdvice, setAiAdvice] = useState("");
  const [site, setSite] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    fetchSettings().then(setSite);
  }, []);

  const runEstimate = async () => {
    if (!aiDesc.trim() || aiLoading) return;
    setAiLoading(true);
    setAiAdvice("");
    try {
      const { data } = await axios.post(`${API}/estimate`, { description: aiDesc.trim(), lang });
      const volumeIdx = { small: 0, medium: 1, large: 2 }[data.volume] ?? 0;
      setForm((f) => ({
        ...f,
        volume: c.volumes[volumeIdx] || f.volume,
        varieties: data.varieties.length > 0 ? data.varieties : f.varieties,
        message: data.message || f.message,
      }));
      setAiAdvice(data.advice);
    } catch {
      toast.error(c.toastError, { description: t.estimate.error });
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    const prefill = (u) => {
      if (u) setForm((f) => ({ ...f, name: f.name || u.name || "", email: f.email || u.email || "" }));
    };
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => prefill(r.data)).catch(() => {});
    const onAuthChange = (e) => prefill(e.detail);
    window.addEventListener("my-auth-changed", onAuthChange);
    return () => window.removeEventListener("my-auth-changed", onAuthChange);
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, volume: c.volumes[0] }));
  }, [c]);

  const kgPerMonth = useMemo(() => Math.round(cups * 8 * 30 / 100) / 10, [cups]);

  const CONTACTS = [
    { icon: Mail, label: c.coords.email, value: site.contact_email, href: `mailto:${site.contact_email}`, testid: "contact-info-email" },
    { icon: Phone, label: c.coords.phone, value: site.contact_phone, href: `tel:${site.contact_phone.replace(/\s/g, "")}`, testid: "contact-info-phone" },
    { icon: Globe, label: c.coords.website, value: site.contact_website, href: `https://${site.contact_website}`, testid: "contact-info-website" },
  ];

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleVariety = (v) =>
    setForm((f) => ({
      ...f,
      varieties: f.varieties.includes(v)
        ? f.varieties.filter((x) => x !== v)
        : [...f.varieties, v],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(c.toastSuccess, { description: c.toastSuccessDesc, duration: 8000 });
      setForm({
        name: "",
        business: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        postal_code: "",
        country: "",
        volume: c.volumes[0],
        varieties: [],
        message: "",
        sample_box: false,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const fieldErrors = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
        : typeof detail === "string" ? detail : "";
      setError(`${c.toastError} — ${fieldErrors || c.toastErrorDesc}`);
      toast.error(c.toastError, { description: fieldErrors || c.toastErrorDesc, duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 md:py-36">
      <div className="grid gap-14 lg:grid-cols-5 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-2"
        >
          <div className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-copper">
            {c.eyebrow}
          </div>
          <h2 className="font-serif text-3xl leading-tight tracking-tight text-pine sm:text-5xl">
            {c.titleA}<span className="italic text-copper">{c.titleEm}</span>
          </h2>
          <p className="mt-6 text-base font-light leading-relaxed text-sand">
            {c.text}
          </p>

          <div className="mt-10 border border-pine/10 bg-paper p-6 shadow-sm" data-testid="contact-info-card">
            <div className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-copper">
              {c.coordsTitle}
            </div>
            <div className="flex flex-col gap-4">
              {CONTACTS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={item.testid}
                  className="group flex items-center gap-4 transition-colors"
                >
                  <span className="flex h-10 w-10 items-center justify-center border border-copper/30 bg-cream transition-colors duration-300 group-hover:bg-copper">
                    <item.icon className="h-4 w-4 text-copper transition-colors duration-300 group-hover:text-paper" />
                  </span>
                  <span>
                    <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-sand">
                      {item.label}
                    </span>
                    <span className="text-sm text-pine group-hover:text-copper">{item.value}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div
            data-testid="volume-estimator"
            className="mt-8 border border-copper/25 bg-paper p-6 shadow-sm sm:p-8"
          >
            <div className="flex items-center gap-3">
              <Coffee className="h-5 w-5 text-copper" />
              <h3 className="font-serif text-xl text-pine">
                {c.estimatorTitle}
              </h3>
            </div>
            <div className="mt-8">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {c.cupsLabel}
                </span>
                <span
                  data-testid="estimator-cups-value"
                  className="font-mono text-2xl font-light text-pine"
                >
                  {cups}
                </span>
              </div>
              <input
                type="range"
                min="50"
                max="2000"
                step="10"
                value={cups}
                data-testid="estimator-cups-slider"
                onChange={(e) => setCups(Number(e.target.value))}
                className="estimator-range mt-4 w-full"
                style={{ "--fill": `${((cups - 50) / 1950) * 100}%` }}
                aria-label={c.cupsLabel}
              />
              <div className="mt-8 border-t border-pine/10 pt-6 text-center">
                <div
                  data-testid="estimator-kg-value"
                  className="font-mono text-4xl font-light text-copper sm:text-5xl"
                >
                  ≈ {kgPerMonth.toLocaleString("fr-FR")} kg
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {c.kgLabel}
                </div>
              </div>
            </div>
          </div>

          <div
            data-testid="ai-estimator"
            className="mt-8 border border-copper/25 bg-paper p-6 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-copper" />
              <h3 className="font-serif text-xl text-pine">{t.estimate.title}</h3>
            </div>
            <textarea
              data-testid="ai-estimate-input"
              rows={2}
              value={aiDesc}
              onChange={(e) => setAiDesc(e.target.value)}
              placeholder={t.estimate.placeholder}
              className="mt-4 w-full resize-none border border-pine/15 bg-white px-4 py-3 text-sm font-light text-bark placeholder:text-sand/60 focus:border-copper focus:outline-none transition-colors duration-300"
            />
            <button
              type="button"
              data-testid="ai-estimate-button"
              onClick={runEstimate}
              disabled={aiLoading || !aiDesc.trim()}
              className="mt-3 inline-flex items-center gap-2 bg-pine px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-copper disabled:opacity-60"
            >
              {aiLoading ? t.estimate.analyzing : t.estimate.button}
            </button>
            {aiAdvice && (
              <p data-testid="ai-estimate-advice" className="mt-4 border-l-2 border-copper bg-cream px-4 py-3 text-sm font-light leading-relaxed text-bark">
                {aiAdvice}
              </p>
            )}
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          data-testid="contact-form"
          className={`border border-pine/10 bg-paper p-6 shadow-sm sm:p-10 lg:col-span-3 ${sent ? "hidden" : ""}`}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {c.name}
              </label>
              <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="María Hernández" />
            </div>
            <div>
              <label htmlFor="contact-business" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {c.business}
              </label>
              <input id="contact-business" data-testid="contact-business-input" required value={form.business} onChange={set("business")} className={inputCls} placeholder="Café Aurora" />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {c.email}
              </label>
              <input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={inputCls} placeholder="maria@cafeaurora.com" />
            </div>
            <div>
              <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {c.phone}
              </label>
              <input id="contact-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={c.phonePlaceholder} />
            </div>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="contact-address" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {f.address}
              </label>
              <input id="contact-address" data-testid="contact-address-input" value={form.address} onChange={set("address")} className={inputCls} placeholder="123 Rue Principale" autoComplete="street-address" />
            </div>
            <div>
              <label htmlFor="contact-city" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {f.city}
              </label>
              <input id="contact-city" data-testid="contact-city-input" value={form.city} onChange={set("city")} className={inputCls} placeholder="Tulancingo" autoComplete="address-level2" />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-postal" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {f.postal}
                </label>
                <input id="contact-postal" data-testid="contact-postal-input" value={form.postal_code} onChange={set("postal_code")} className={inputCls} placeholder="43600" autoComplete="postal-code" />
              </div>
              <div>
                <label htmlFor="contact-country" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {f.country}
                </label>
                <input id="contact-country" data-testid="contact-country-input" value={form.country} onChange={set("country")} className={inputCls} placeholder="Mexique" autoComplete="country-name" />
              </div>
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="contact-volume" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
              {c.volume}
            </label>
            <select id="contact-volume" data-testid="contact-volume-select" value={form.volume} onChange={set("volume")} className={inputCls}>
              {c.volumes.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div className="mt-5">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
              {c.varietiesLabel}
            </span>
            <div className="flex flex-wrap gap-2">
              {VARIETY_OPTIONS.map((v) => {
                const active = form.varieties.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`contact-variety-${v.toLowerCase()}`}
                    onClick={() => toggleVariety(v)}
                    aria-pressed={active}
                    className={`border px-4 py-2 text-xs tracking-wide transition-all duration-300 ${
                      active
                        ? "border-copper bg-copper text-paper"
                        : "border-pine/20 text-sand hover:border-copper/60 hover:text-pine"
                    }`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
              {c.messageLabel}
            </label>
            <textarea id="contact-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder={c.messagePlaceholder} />
          </div>

          <label
            htmlFor="contact-sample-box"
            className="mt-6 flex cursor-pointer items-center gap-3 border border-copper/25 bg-cream px-4 py-3 transition-colors duration-300 hover:border-copper/50"
          >
            <input
              id="contact-sample-box"
              type="checkbox"
              data-testid="contact-sample-box-checkbox"
              checked={form.sample_box}
              onChange={set("sample_box")}
              className="h-4 w-4 accent-[#B0682C]"
            />
            <span className="flex items-center gap-2 text-sm font-light text-bark">
              <Package className="h-4 w-4 shrink-0 text-copper" />
              {c.sampleBox}
            </span>
          </label>

          {error && (
            <p data-testid="contact-error" className="mt-6 border border-copper/40 bg-cream px-4 py-3 text-sm font-light text-copper">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="contact-form-submit-button"
            disabled={sending}
            className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-copper px-7 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-paper transition-colors duration-300 hover:bg-pine disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {sending ? c.sending : c.submit}
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>

        {sent && (
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid="contact-success"
            className="border border-copper/30 bg-paper p-10 text-center shadow-sm sm:p-16 lg:col-span-3"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-copper" />
            <h3 className="mt-6 font-serif text-3xl tracking-tight text-pine sm:text-4xl">
              {c.successTitle}
            </h3>
            <p className="mx-auto mt-5 max-w-md text-sm font-light leading-relaxed text-sand sm:text-base">
              {c.successText}
            </p>
            <button
              type="button"
              data-testid="contact-success-again"
              onClick={() => setSent(false)}
              className="mt-10 border border-pine/25 px-7 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-pine transition-colors duration-300 hover:border-copper hover:text-copper"
            >
              {c.successAgain}
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
